/** 月次人員棚卸(入退場者管理表)。2段ヘッダーはテンプレートごとに違うので素の table で組む。 */
import { Alert, Button, Card, DatePicker, Flex, Space, Spin, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { type CSSProperties, useState } from "react";

import type { MonthlyKind, MonthlyOut, MonthlyRow, MonthlyTable } from "../../lib/api";
import { MONTHLY_CELL_COLOR, monthlyStateColor } from "./constants";
import { errorText } from "./hooks";
import { useDownload, useMonthly } from "./loanHooks";

const CELL: CSSProperties = {
  border: "1px solid #f0f0f0",
  padding: "4px 8px",
  whiteSpace: "nowrap",
  fontSize: 12,
};
const HEAD: CSSProperties = { ...CELL, background: "#fafafa", fontWeight: 600 };

/** セルの中身。入力欄は値、SaaS 項目・工程は状態(空 = 不要)。 */
function Cell({ cell, data }: { cell: MonthlyRow["cells"][number]; data: MonthlyOut }) {
  if (cell.kind === "field") return <span>{cell.text || "—"}</span>;
  const label = data.status_text[cell.status] ?? data.status_undone;
  const color = MONTHLY_CELL_COLOR[cell.status];
  return color ? <Tag color={color}>{label}</Tag> : <Tag>{label}</Tag>;
}

function FlowTable({
  data,
  table,
  kind,
  month,
}: {
  data: MonthlyOut;
  table: MonthlyTable;
  kind: MonthlyKind;
  month: string;
}) {
  const download = useDownload();
  const tid = table.template_id;
  const suffix = `month=${month}${tid ? `&template_id=${tid}` : ""}`;

  return (
    <Card
      size="small"
      style={{ marginBottom: 16 }}
      title={
        <Space>
          <span>{table.template}</span>
          {table.is_default && <Tag color="blue">既定</Tag>}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {table.rows.length} 件
          </Typography.Text>
        </Space>
      }
      extra={
        tid ? (
          <Space>
            <Button size="small" onClick={() => void download(`/api/hr/monthly.csv?${suffix}`)}>
              CSV
            </Button>
            <Button size="small" onClick={() => void download(`/api/hr/monthly.xlsx?${suffix}`)}>
              Excel
            </Button>
          </Space>
        ) : null
      }
    >
      {table.rows.length === 0 && (
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          この月の対象なし(既定テンプレートなので列だけ出している)。
        </Typography.Paragraph>
      )}
      <div style={{ overflowX: "auto" }} data-monthly-table={tid ?? 0}>
        <table style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {data.head.map((h) => (
                <th key={h} rowSpan={2} style={HEAD}>
                  {h}
                </th>
              ))}
              <th rowSpan={2} style={HEAD}>
                {kind.date_label}
              </th>
              {table.top.map((h, i) => (
                <th key={`${h.name}-${i}`} colSpan={h.colspan} rowSpan={h.rowspan} style={HEAD}>
                  {h.name}
                </th>
              ))}
              <th rowSpan={2} style={HEAD}>
                状態
              </th>
              <th rowSpan={2} style={HEAD}>
                申請
              </th>
            </tr>
            <tr>
              {table.sub.map((col, i) => (
                <th key={`${col.section}-${col.name}-${i}`} style={HEAD}>
                  {col.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row) => (
              <tr key={row.request_id}>
                <td style={CELL}>{row.no}</td>
                <td style={CELL}>
                  <Link to={`/hr/employees/${row.employee_id}`}>{row.name}</Link>
                </td>
                <td style={CELL}>{row.email || "—"}</td>
                <td style={CELL}>{row.department || "—"}</td>
                <td style={CELL}>{row.employment_type || "—"}</td>
                <td style={CELL}>{row.start_date || "—"}</td>
                {row.cells.map((cell, i) => (
                  <td key={i} style={CELL}>
                    <Cell cell={cell} data={data} />
                  </td>
                ))}
                <td style={CELL}>
                  <Tag color={monthlyStateColor(row.state)}>{row.state_label}</Tag>
                  <Typography.Text type="secondary">
                    {row.done}/{row.total}
                  </Typography.Text>
                </td>
                <td style={CELL}>
                  <Link to={`/hr/wf-requests/${row.request_id}`}>#{row.request_id}</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function MonthlyPage() {
  const [month, setMonth] = useState("");
  const query = useMonthly(month);
  const download = useDownload();
  const data = query.data;
  const shown = data?.month ?? month;

  return (
    <div>
      <Flex align="center" justify="space-between" style={{ marginBottom: 16 }} gap={12} wrap>
        <Typography.Title level={3} style={{ margin: 0 }}>
          月次人員棚卸
        </Typography.Title>
        <Space wrap>
          <Button onClick={() => setMonth(data?.prev_month ?? "")} disabled={!data}>
            ← {data?.prev_month ?? ""}
          </Button>
          <Typography.Text strong data-monthly-month>
            {shown}
          </Typography.Text>
          <Button onClick={() => setMonth(data?.next_month ?? "")} disabled={!data}>
            {data?.next_month ?? ""} →
          </Button>
          <DatePicker
            picker="month"
            value={shown ? dayjs(shown + "-01") : null}
            onChange={(d) => setMonth(d ? d.format("YYYY-MM") : "")}
            placeholder="今月"
          />
          <Button
            onClick={() => void download(`/api/hr/monthly.csv?month=${shown}`)}
            disabled={!data}
          >
            この月を CSV
          </Button>
          <Button
            onClick={() => void download(`/api/hr/monthly.xlsx?month=${shown}`)}
            disabled={!data}
          >
            この月を Excel
          </Button>
        </Space>
      </Flex>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          対象月 = 入場日 / 退場日(異動は<strong>着任日の前月</strong>。日付が空の申請は申請月。
          境界は JST)。列はテンプレートごとに違うので表を分けている。1段目がセクション、
          2段目がその子の列。セルは 済 / 未 / 停止 / 不要(申請時に対象外)。列の定義は
          <Link to="/hr/workflow">WF設計</Link>で変える。
        </Typography.Text>
      </Card>

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="月次人員棚卸を取得できなかった"
          description={errorText(query.error)}
        />
      )}

      {query.isFetching && !data && (
        <Flex justify="center" style={{ padding: 64 }}>
          <Spin />
        </Flex>
      )}

      {data?.kinds.map((kind) => (
        <div key={kind.kind} style={{ marginBottom: 24 }}>
          <Typography.Title level={4} data-monthly-kind={kind.kind}>
            ▼ {kind.label}
            {kind.kind === "transfer" && (
              <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                (着任日の前月に載せる)
              </Typography.Text>
            )}
          </Typography.Title>
          {kind.tables.length === 0 ? (
            <Typography.Text type="secondary">この月の対象なし。</Typography.Text>
          ) : (
            kind.tables.map((table) => (
              <FlowTable
                key={`${table.template_id ?? table.template}`}
                data={data}
                table={table}
                kind={kind}
                month={data.month}
              />
            ))
          )}
        </div>
      ))}
    </div>
  );
}
